import React, { useState, useEffect } from 'react';
import Grid from "@mui/material/Grid";
import { Modal } from "@mui/material";
import MDBox from "components/atoms/MDBox/MDBox";
import MDTypography from "components/atoms/MDTypography/MDTypography";

// Custom components
import { Tabs, TabList, Tab, TabPanels, TabPanel, TabItem } from 'components/organisms/Tabs';
import TriageDataTable from 'components/organisms/TriageDataTable/TriageDataTable';
import PatientDataTable from 'components/organisms/PatientDataTable/PatientDataTable';
import AddPatientModal from 'components/organisms/AddPatientModal/AddPatientModal';

// Layout
import TriageFlowLayout from 'components/templates/TriageFlowLayout/TriageFlowLayout';

// Types and styling
import { TableColumnType as TriageTableColumnType } from 'components/organisms/DataTable';
import { TableColumnType as PatientTableColumnType } from 'components/organisms/DataTable';
import { MUIColors } from 'models/StyleModels';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArchive, faEye } from '@fortawesome/free-solid-svg-icons';
import MDButton from 'components/atoms/MDButton/MDButton';
import { TriageXrayStatus } from "models/AppointmentModels";
import colors from "assets/theme/base/colors";
import { useNavigation } from "hooks/useNavigation";
import { RouteKeys } from "models/RouteModels";
import { useBreakpoints } from 'hooks/useBreakpoints';
import { Breakpoints } from 'models/StyleModels';

// Data hooks
import { useAuth } from 'hooks/useAuth';
import { usePaginatedAppointmentData } from 'hooks/usePaginatedAppointmentData';
import { usePaginatedPatientData } from 'hooks/usePaginatedPatientData';
import { useOrgData } from 'hooks/useOrgData';

import { Patient } from 'models/PatientModels';
import { usePatientMutations } from 'hooks/usePatientMutations';
import DateDisplay from 'components/atoms/DateDisplay/DateDisplay';
import { Card, Container } from '@mui/material';
import Alert from 'components/atoms/Alert/Alert';
import Spinner from 'components/molecules/Spinner/Spinner';
import { NameCellValue, DateButton, ErrorMessage } from './TriageListPage.styles';
import StatusCell from './StatusCell';
import SelectedJointsCell from './SelectedJointsCell';
import { useNavigate } from 'react-router-dom';
import { routesMap } from 'data/routes';
import TriageListAppBar from 'components/pages/TriageListPage/TriageListAppBar';
import { useDebounce } from 'hooks/useDebounce';



interface TriageListContentProps {
  allAppointments: any[];
  allPatients: any[];
  user: any;
  org: any;
  refetchAllAppointments: () => Promise<any>;
  refetchAllPatients: () => Promise<any>;
  // Appointment pagination props
  isFetchingNextPageAppointments?: boolean;
  hasNextPageAppointments?: boolean;
  fetchNextPageAppointments?: () => void;
  totalCountAppointments?: number | null;
  appointmentsLoading?: boolean;
  // Patient pagination props
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  totalCount?: number | null;
  totalReturned?: number;
  patientsLoading?: boolean;
  patientsInitialLoading?: boolean;
  // Search props
  onAppointmentSearchChange?: (query: string) => void;
  onPatientSearchChange?: (query: string) => void;
  appointmentSearchInput?: string;
  // Date range props
  onAppointmentDateRangeChange?: (dateRange: { startDate: Date | null; endDate: Date | null }) => void;
  appointmentDateRange?: { startDate: Date | null; endDate: Date | null };
}


const TriageListContent: React.FC<TriageListContentProps> = ({
  allAppointments,
  allPatients,
  user,
  org,
  refetchAllAppointments,
  refetchAllPatients,
  isFetchingNextPageAppointments,
  hasNextPageAppointments,
  fetchNextPageAppointments,
  totalCountAppointments,
  appointmentsLoading,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
  totalCount,
  totalReturned,
  patientsLoading,
  patientsInitialLoading,
  onAppointmentSearchChange,
  onPatientSearchChange,
  appointmentSearchInput,
  onAppointmentDateRangeChange,
  appointmentDateRange,
}) => {
  // Use localStorage to persist tab state across re-renders and page reloads
  const getInitialTab = () => {
    try {
      return localStorage.getItem('triage-active-tab') || 'triage';
    } catch {
      return 'triage';
    }
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [addPatientModalOpen, setAddPatientModalOpen] = useState<boolean>(false);
  const [editPatientModalOpen, setEditPatientModalOpen] = useState<boolean>(false);
  const [addExamModalOpen, setAddExamModalOpen] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [archiveModalOpen, setArchiveModalOpen] = useState<boolean>(false);
  const [archiveTargetPatient, setArchiveTargetPatient] = useState<Patient | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [recentlyArchivedPatient, setRecentlyArchivedPatient] = useState<Patient | null>(null);
  const [undoTimeoutId, setUndoTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const { navigateToBaseRouteWithAptmtDetails } = useNavigation();
  const { breakpointBreached: lgBreakpointBreached } = useBreakpoints({ breakpoint: Breakpoints.LARGE });
  const navigate = useNavigate();
  const {
    archivePatient,
    archivePatientLoading,
    unarchivePatient,
    unarchivePatientLoading
  } = usePatientMutations();

  // Persist tab state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('triage-active-tab', activeTab);
    } catch {
      // Ignore localStorage errors
    }
  }, [activeTab]);


  const handleUndoArchive = async () => {
    if (!recentlyArchivedPatient?.patient_id) return;

    try {
      await unarchivePatient(recentlyArchivedPatient.patient_id);
      setRecentlyArchivedPatient(null);
      if (undoTimeoutId) {
        clearTimeout(undoTimeoutId);
        setUndoTimeoutId(null);
      }
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    } catch (error) {
      console.error('Failed to undo archive:', error);
    }
  };

  useEffect(() => {
    return () => {
      if (undoTimeoutId) {
        clearTimeout(undoTimeoutId);
      }
    };
  }, [undoTimeoutId]);





  // Helper function for Exam Room button
  const ExamRoomButton = (row: any) => {
    const appointment = row.original.json; // Access the appointment object directly
    if (!appointment) return null;

    if (appointment?.xray_status === TriageXrayStatus.READY_FOR_REVIEW || appointment?.xray_status === TriageXrayStatus.COMPLETED) {

      return (
        <MDButton
          variant='contained'
          size={lgBreakpointBreached ? 'small' : 'medium'}
          onClick={() => {
            navigateToBaseRouteWithAptmtDetails({
              routeKey: RouteKeys.EXAM_ROOM_ALL_JOINTS,
              appointmentId: appointment.appointment_id,
              appointmentDateISOString: appointment.appointment_date,
              addtlQueryParams: { flow: 'TRIAGE' }
            })
          }}
          color={MUIColors.SECONDARY}
        >
          <FontAwesomeIcon color={colors.white.focus} icon={faEye} style={{ marginRight: '10px' }} />
          Exam Room
        </MDButton>
      )
    }

    return (
      <MDButton
        variant='contained'
        size={lgBreakpointBreached ? 'small' : 'medium'}
        color={MUIColors.SECONDARY}
        disabled
        style={{
          background: colors.grey[200],
          color: colors.grey[600],
          border: `1px solid ${colors.grey[400]}`,
        }}
      >
        <FontAwesomeIcon color={colors.grey[600]} icon={faEye} style={{ marginRight: '10px' }} />
        Exam Room
      </MDButton>
    )
  }

  // Sample data for demonstration with triage-specific columns
  const sampleTriageColumns: TriageTableColumnType[] = [
    {
      Header: 'Patient Name',
      accessor: 'patient_name',
      width: '15%',
      align: 'left',
      canSort: true,
      Cell: ({ row }: any) => {
        const appointment = row.original.json;
        const isDisabled = !appointment;

        return (
          <NameCellValue
            disabled={isDisabled}
            onClick={() => {
              if (appointment) {
                // Create a proper patient object from the appointment data
                const patientData = {
                  patient_id: appointment.patient_id,
                  first_name: appointment.patient_info?.patient_name?.split(' ')[0] || '',
                  last_name: appointment.patient_info?.patient_name?.split(' ').slice(1).join(' ') || '',
                  external_id: appointment.patient_info?.external_id || '',
                  date_of_birth: appointment.patient_info?.date_of_birth || '',
                  provider: appointment.patient_info?.provider || '',
                };
                setSelectedPatient({ ...appointment, patient_info: patientData });
                setEditPatientModalOpen(true);
              }
            }}
          >
            {row.original.patient_name}
          </NameCellValue>
        );
      }
    },
    {
      width: '15%',
      align: 'left',
      Header: 'Date of Appt.', Cell(props) {
        const appointmentDate = props.row.original.json?.appointment_date;

        if (!appointmentDate) {
          return <span>-</span>;
        }

        return <DateDisplay isoString={appointmentDate} showTime={false} />
      },
    },
    {
      Header: 'PROs',
      accessor: 'exam_room',
      width: '15%',
      align: 'center',
      Cell: ({ row }: any) => (
        <StatusCell
          appointment={row.original.json}
          kind='PROS'
          lgBreakpointBreached={lgBreakpointBreached}
          navigateToBaseRouteWithAptmtDetails={navigateToBaseRouteWithAptmtDetails}
        />
      )
    },
    {
      Header: 'X-rays',
      accessor: 'exam_room',
      width: '15%',
      align: 'center',
      Cell: ({ row }: any) => (
        <StatusCell
          appointment={row.original.json}
          kind='XRAYS'
          lgBreakpointBreached={lgBreakpointBreached}
          navigateToBaseRouteWithAptmtDetails={navigateToBaseRouteWithAptmtDetails}
        />
      )
    },
    {
      Header: 'Selected Joints',
      accessor: 'exam_room',
      width: '15%',
      align: 'left',
      Cell: ({ row }: any) => <SelectedJointsCell appointment={row.original.json} />
    },
    {
      Header: '',
      accessor: 'exam_room',
      width: '170px',
      align: 'right',
      Cell: ({ row }: any) => ExamRoomButton(row)
    }
  ];

  const samplePatientColumns: PatientTableColumnType[] = [
    { Header: 'Patient Name', accessor: 'name', width: '180px', align: 'left', canSort: true },
    { Header: 'ID Number', accessor: 'id_number', width: '120px', align: 'left' },
    {
      width: 'auto',
      align: 'left',
      Header: 'Last Evaluation', Cell(props) {
        const latestEvaluation = props.row.original.json?.latest_evaluation;
        const appointmentId = latestEvaluation?.appointment_id;
        const appointmentDate = latestEvaluation?.appointment_date;

        if (!appointmentDate || !appointmentId) {
          return <span>-</span>;
        }

        return <DateButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigateToBaseRouteWithAptmtDetails({
              routeKey: RouteKeys.EXAM_ROOM_ALL_JOINTS,
              appointmentId: appointmentId,
              appointmentDateISOString: appointmentDate,
              addtlQueryParams: { flow: 'TRIAGE' }
            })
          }}>
          <DateDisplay isoString={appointmentDate} showTime={false} />
        </DateButton >
      },
    },
    {
      accessor: 'data',
      width: '15%',
      align: 'left',
      Header: '',
      Cell(props) {
        return <MDButton variant='contained' color={MUIColors.SECONDARY} onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const patientId = props.row.original.json?.patient_id;
          if (patientId) {
            navigate(`${routesMap[RouteKeys.PATIENT_OVERVIEW].baseRoute}/${patientId}`);
          }
        }}>
          Patient Profile
        </MDButton>
      },
    },
    {
      accessor: 'data',
      width: '18%',
      align: 'left',
      Header: '',
      Cell(props) {
        const patient: Patient = props.row.original.json;

        if (!patient?.patient_id) {
          return null;
        }

        const openArchiveModal = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          setArchiveTargetPatient(patient);
          setArchiveModalOpen(true);
        };

        return <MDButton
          variant='outlined'
          color={MUIColors.SECONDARY}
          onClick={openArchiveModal}
        >
          <FontAwesomeIcon color={colors.secondary.focus} icon={faArchive} style={{ marginRight: '10px' }} />
          Archive Patient
        </MDButton>
      },
    },
  ];

  const generateTriageData = () => {
    const appointmentsToUse = allAppointments || [];
    const data = appointmentsToUse?.sort((a, b) => (new Date(b.created_at) as any) - (new Date(a.created_at) as any)).map((appointment) => ({
      json: appointment,
      patient_name: appointment.patient_info?.patient_name || 'Unknown Patient',
      id_number: appointment.patient_info?.external_id || 'N/A',
      created_at: appointment.created_at,
      patient_id: appointment.patient_id,
      appointment_id: appointment.appointment_id,
    })) || [];

    return data;
  };

  const generatePatientData = () => {
    return allPatients?.map((patient) => ({
      json: patient, // Include the full patient object for handleRowClick
      name: `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim() || 'Unknown Patient',
      dob: patient?.date_of_birth ? new Date(patient.date_of_birth).toLocaleDateString() : 'N/A',
      id_number: patient?.external_id || '-',
      patient_profile_link: patient?.patient_id ? `${routesMap[RouteKeys.PATIENT_OVERVIEW].baseRoute}/${patient.patient_id}` : '#',
    })) || [];
  };

  const triageData = generateTriageData();
  const patientData = generatePatientData();



  const handlePatientRowClick = (row: { json: Patient }) => {
    const patientId = row.json?.patient_id;
    if (patientId) {
      navigate(`${routesMap[RouteKeys.PATIENT_OVERVIEW].baseRoute}/${patientId}`);
    }
  };

  const tabData: TabItem[] = [
    {
      id: 'triage',
      label: 'Evaluations',
      content: (
        <TriageDataTable
          canSearch={true}
          showTotalEntries={false}
          table={{
            columns: sampleTriageColumns,
            rows: triageData,
          }}
          isSorted={[true, true, true, false, false, false, false]}
          emptyMessage="No triage data available"
          org={org}
          user={user}
          verticalAlign="top"
          isFetchingNextPage={isFetchingNextPageAppointments}
          hasNextPage={hasNextPageAppointments}
          onLoadMore={fetchNextPageAppointments}
          totalCount={totalCountAppointments || undefined}
          totalReturned={allAppointments?.length}
          pageSize={15}
          dataLoading={appointmentsLoading}
          onAddExam={() => setAddExamModalOpen(true)}
          onSearchChange={onAppointmentSearchChange}
          searchValue={appointmentSearchInput}
          useServerSideSearch={true}
          onDateRangeChange={onAppointmentDateRangeChange}
          dateRange={appointmentDateRange}
        />
      ),
    },
    {
      id: 'patients',
      label: 'Patient List',
      content: (
        <PatientDataTable
          showTotalEntries={false}
          table={{
            columns: samplePatientColumns,
            rows: patientData,
          }}
          isSorted={[true, false, false, false, false]}
          emptyMessage="No patients found"
          handleRowClick={handlePatientRowClick}
          user={user}
          onAddPatient={() => setAddPatientModalOpen(true)}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          onLoadMore={onLoadMore}
          totalCount={totalCount}
          totalReturned={totalReturned}
          pageSize={15}
          dataLoading={patientsInitialLoading}
          onSearchChange={onPatientSearchChange}
          useServerSideSearch={true}
        />
      ),
    },
  ];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  return (
    <Container>
      <Grid container spacing={3} paddingTop={3}>
        <Grid item xs={12}>
          <Tabs
            tabs={tabData}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            variant="default"
            orientation="horizontal"
          >
            <TriageListAppBar
              tabList={(
                <TabList>
                  {tabData.map((tab) => (
                    <Tab key={tab.id} id={tab.id} disabled={tab.disabled}>
                      {tab.label}
                    </Tab>
                  ))}
                </TabList>
              )}
            />
            <Card>

              <TabPanels>
                {tabData.map((tab) => (
                  <TabPanel key={tab.id} id={tab.id}>
                    {tab.content}
                  </TabPanel>
                ))}
              </TabPanels>
            </Card>
          </Tabs>
        </Grid>
      </Grid>

      {addPatientModalOpen && (
        <AddPatientModal
          onSuccess={() => {
            setAddPatientModalOpen(false);
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 3000);
          }}
          closeModal={() => setAddPatientModalOpen(false)}
          refetchTriageAppointmentStatuses={refetchAllAppointments} />
      )}

      {editPatientModalOpen && selectedPatient && (
        <AddPatientModal
          isEdit
          onSuccess={() => {
            setEditPatientModalOpen(false);
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 3000);
          }}
          appointmentDate={selectedPatient.appointment_date}
          appointmentId={selectedPatient.appointment_id}
          patient={selectedPatient.patient_info}
          closeModal={() => setEditPatientModalOpen(false)}
          refetchTriageAppointmentStatuses={refetchAllAppointments} />
      )}

      {addExamModalOpen && (
        <AddPatientModal
          onSuccess={() => {
            setAddExamModalOpen(false);
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 3000);
          }}
          closeModal={() => setAddExamModalOpen(false)}
          refetchTriageAppointmentStatuses={refetchAllAppointments} />
      )}

      <Modal open={archiveModalOpen} onClose={() => {
        setArchiveModalOpen(false);
        setArchiveError(null);
      }}>
        <MDBox display="grid" justifyContent="center" alignItems="center" minHeight="50vh">
          <Card sx={{ padding: '2rem', width: '90%', maxWidth: '28rem' }}>
            <MDTypography variant='h4' color={MUIColors.SECONDARY} sx={{ marginBottom: '1rem' }}>
              Confirm Archive
            </MDTypography>
            <MDTypography variant='body1' sx={{ marginBottom: '1.5rem' }}>
              Are you sure you want to archive this patient?
            </MDTypography>
            {archiveError && (
              <ErrorMessage>
                {archiveError}
              </ErrorMessage>
            )}
            <MDBox display="flex" justifyContent="flex-end" gap="1rem">
              <MDButton variant='outlined' color={MUIColors.SECONDARY} onClick={() => {
                setArchiveModalOpen(false);
                setArchiveError(null);
              }}>
                Cancel
              </MDButton>
              <MDButton
                variant='contained'
                color={MUIColors.SECONDARY}
                disabled={archivePatientLoading}
                onClick={async () => {
                  if (!archiveTargetPatient?.patient_id) return;

                  try {
                    setArchiveError(null);
                    await archivePatient(archiveTargetPatient.patient_id);

                    // Set up undo functionality
                    setRecentlyArchivedPatient(archiveTargetPatient);
                    const timeoutId = setTimeout(() => {
                      setRecentlyArchivedPatient(null);
                      setUndoTimeoutId(null);
                    }, 10000); // 10 seconds to undo
                    setUndoTimeoutId(timeoutId);

                    setArchiveModalOpen(false);
                    setArchiveTargetPatient(null);
                    setShowAlert(true);
                    setTimeout(() => setShowAlert(false), 3000);
                  } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to archive patient. Please try again.';
                    setArchiveError(errorMessage);
                  }
                }}
              >
                {archivePatientLoading ? 'Archiving...' : 'Archive'}
              </MDButton>
            </MDBox>
          </Card>
        </MDBox>
      </Modal>

      <Alert show={showAlert} handleClose={() => setShowAlert(false)}>
        {recentlyArchivedPatient ? (
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap="1rem">
            <span>Patient archived successfully.</span>
            <MDButton
              variant="outlined"
              size="small"
              color={MUIColors.SECONDARY}
              disabled={unarchivePatientLoading}
              onClick={handleUndoArchive}
              sx={{ ml: 2, minWidth: '80px' }}
            >
              {unarchivePatientLoading ? 'Undoing...' : 'Undo'}
            </MDButton>
          </MDBox>
        ) : (
          'Patient restored successfully.'
        )}
      </Alert>


    </Container>
  );
};

const TriageListFallback: React.FC = () => {
  return (
    <TriageFlowLayout title="Triage Dashboard">
      <MDBox>
        <Spinner />
      </MDBox>
    </TriageFlowLayout>
  );
};

const TriageListPage: React.FC = () => {
  const { user } = useAuth();
  const { org } = useOrgData();

  // Search state
  const [appointmentSearchInput, setAppointmentSearchInput] = useState('');
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const appointmentSearchQuery = useDebounce(appointmentSearchInput, 300);

  // Date range state for appointments
  const [appointmentDateRange, setAppointmentDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });

  const {
    allAppointments,
    appointmentsInitialLoading,
    refetchAppointments,
    isFetchingNextPage: isFetchingNextPageAppointments,
    hasNextPage: hasNextPageAppointments,
    fetchNextPage: fetchNextPageAppointments,
    totalCount: totalCountAppointments,
  } = usePaginatedAppointmentData({
    limit: 15,
    includeTotalCount: true,
    polling: true, // AVT REMEMBER TO TURN THIS BACK ON
    includeExams: true,
    searchQuery: appointmentSearchQuery,
    startDate: appointmentDateRange.startDate,
    endDate: appointmentDateRange.endDate,
  });

  const {
    allPatients,
    allPatientsLoading,
    allPatientsInitialLoading,
    refetchAllPatients,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    totalCount,
    totalReturned,
  } = usePaginatedPatientData({
    limit: 15,
    includeTotalCount: true,
    includeLatestEvaluation: true,
    includeArchived: false,
    searchQuery: patientSearchQuery,
  });


  // Always show content - let individual tables handle their own loading
  return (
    <TriageListContent
      allAppointments={allAppointments}
      allPatients={allPatients}
      user={user}
      org={org}
      refetchAllAppointments={refetchAppointments}
      refetchAllPatients={refetchAllPatients}
      appointmentsLoading={appointmentsInitialLoading}
      patientsLoading={allPatientsLoading}
      patientsInitialLoading={allPatientsInitialLoading}
      isFetchingNextPageAppointments={isFetchingNextPageAppointments}
      hasNextPageAppointments={hasNextPageAppointments}
      fetchNextPageAppointments={fetchNextPageAppointments}
      totalCountAppointments={totalCountAppointments}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      onLoadMore={fetchNextPage}
      totalCount={totalCount}
      totalReturned={totalReturned}
      onAppointmentSearchChange={setAppointmentSearchInput}
      appointmentSearchInput={appointmentSearchInput}
      onPatientSearchChange={setPatientSearchQuery}
      onAppointmentDateRangeChange={setAppointmentDateRange}
      appointmentDateRange={appointmentDateRange}
    />
  );
};

export default TriageListPage;